import type { TFunction } from 'i18next';

import type { StoredGenderCode } from '@/lib/gender';

import type { FamilyRelationship } from './member-form-state';

type RelationshipLabelVariant = 'default' | 'my' | 'preview';

const GENDERED_RELATIONSHIP_KEYS: Partial<
  Record<FamilyRelationship, { male: string; female: string; fallback: FamilyRelationship }>
> = {
  uncle_aunt: { male: 'uncle', female: 'aunt', fallback: 'uncle_aunt' },
  niece_nephew: { male: 'nephew', female: 'niece', fallback: 'niece_nephew' },
};

const resolveRelationshipKey = (
  relationship: FamilyRelationship,
  gender: StoredGenderCode | null | undefined,
): string => {
  const gendered = GENDERED_RELATIONSHIP_KEYS[relationship];
  if (!gendered) return relationship;
  if (gender === 'male') return gendered.male;
  if (gender === 'female') return gendered.female;
  return gendered.fallback;
};

export const formatFamilyRelationship = (
  t: TFunction<'people'>,
  relationship: FamilyRelationship,
  gender: StoredGenderCode | null | undefined,
  variant: RelationshipLabelVariant = 'default',
): string => {
  const key = resolveRelationshipKey(relationship, gender);
  if (variant === 'my') return t(`members.relationship.my.${key}`);
  if (variant === 'preview') return t(`members.relationship.preview.${key}`);
  return t(`members.relationship.${key}`);
};

export const formatFamilyRelationshipPreview = (
  t: TFunction<'people'>,
  relationship: FamilyRelationship,
  gender: StoredGenderCode | null | undefined,
  params: { name: string; member: string },
): string => {
  const key = resolveRelationshipKey(relationship, gender);
  return t(`members.relationship.preview.${key}`, params);
};
